import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Form, Alert, Modal } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import PermissionWrapper from "../components/PermissionWrapper";
import PackageService, { Package, PaymentDetails } from "../services/PackageService";
import { fetchMyGroups } from "../services/UserGroupService";
import { useSelector } from "react-redux";
import CommunicationService from "../services/CommunicationService";
import CustomerSupportWidget from "../components/CustomerSupportWidget";

const PaymentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const packageId = searchParams.get('packageId');
  
  const [package_, setPackage] = useState<Package | null>(null);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'upi' | 'netbanking'>('upi');
  const [transactionId, setTransactionId] = useState('');
  const [paymentProof, setPaymentProof] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [userGroups, setUserGroups] = useState<any[]>([]);

  const { user } = useSelector((state: any) => state.auth);

  useEffect(() => {
    if (packageId) {
      fetchPackageDetails();
      fetchPaymentDetails();
      fetchUserGroups();
    }
  }, [packageId]);

  useEffect(() => {
    if (userGroups.length === 1) {
      setSelectedGroupId(userGroups[0]._id);
    }
  }, [userGroups]);

  const fetchPackageDetails = async () => {
    try {
      const data = await PackageService.getPackageById(packageId!);
      setPackage(data);
    } catch (error) {
      console.error('Error fetching package:', error);
      setError('Package not found');
    }
  };

  const fetchPaymentDetails = async () => {
    try {
      const data = await PackageService.getPaymentDetails();
      setPaymentDetails(data);
    } catch (error) {
      console.error('Error fetching payment details:', error);
      setError('Payment details not configured');
    }
  };

  const fetchUserGroups = async () => {
    try {
      const data = await fetchMyGroups(1, 100);
      setUserGroups(data.groups || []);
    } catch (error) {
      console.error('Error fetching user groups:', error);
    }
  };

  const generateUPILink = () => {
    if (!paymentDetails || !package_) return '';
    
    const upiId = paymentDetails.upiId;
    const amount = package_.totalPrice;
    const note = `Payment for ${package_.packageName}`;
    
    return `upi://pay?pa=${upiId}&am=${amount}&tn=${encodeURIComponent(note)}`;
  };

  const handlePaymentSubmit = async () => {
    if (!package_) return;
    
    if (!selectedGroupId) {
      setError('Please select a group');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      await PackageService.submitPaymentRequest({
        packageId: package_._id,
        groupId: selectedGroupId,
        amount: package_.totalPrice,
        paymentMethod,
        transactionId: transactionId || undefined,
        paymentProof: paymentProof || undefined
      });
      
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting payment:', error);
      setError('Failed to submit payment request');
    } finally {
      setLoading(false);
    }
  };

  const notifyAdmin = async (channel: 'sms' | 'whatsapp' | 'email') => {
    if (!package_ || !transactionId) return;
    
    try {
      await CommunicationService.contactSupport(
        user?.firstName || 'User',
        user?.phone || '',
        'Payment Submission',
        `Payment submitted for ${package_.packageName}. Amount: ₹${package_.totalPrice}. Transaction ID: ${transactionId}. Please verify and approve.`,
        channel
      );
    } catch (error) {
      console.error('Error notifying admin:', error);
    }
  };

  if (error) {
    return (
      <Container>
        <Alert variant="danger" className="mt-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!package_ || !paymentDetails) {
    return (
      <Container>
        <div className="text-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <PermissionWrapper pageName="PaymentPage">
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <h2>
              <i className="bi bi-credit-card me-2"></i>
              Complete Payment
            </h2>
          </Col>
        </Row>

        {/* Group Selection */}
        {userGroups.length > 1 && (
          <Row className="mb-4">
            <Col>
              <Card className="border-warning">
                <Card.Header className="bg-warning text-dark">
                  <h5 className="mb-0">
                    <i className="bi bi-people me-2"></i>
                    Select Group
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group>
                    <Form.Label className="fw-semibold">Choose the group for this package *</Form.Label>
                    <Form.Select
                      value={selectedGroupId}
                      onChange={(e) => setSelectedGroupId(e.target.value)}
                      required
                    >
                      <option value="">Select a group...</option>
                      {userGroups.map((group) => (
                        <option key={group._id} value={group._id}>
                          {group.name} - {group.groupCode}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {userGroups.length === 0 && (
          <Row className="mb-4">
            <Col>
              <Alert variant="warning">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>No Groups Found:</strong> You need to be a member of at least one group to purchase packages.
              </Alert>
            </Col>
          </Row>
        )}

        <Row>
          {/* Package Details */}
          <Col md={6}>
            <Card className="mb-4">
              <Card.Header>
                <h5>Package Details</h5>
              </Card.Header>
              <Card.Body>
                <h6>{package_.packageName}</h6>
                {selectedGroupId && (
                  <div className="mb-2">
                    <small className="text-muted">
                      <i className="bi bi-people me-1"></i>
                      <strong>Group:</strong> {userGroups.find(g => g._id === selectedGroupId)?.name}
                    </small>
                  </div>
                )}
                <div className="mb-2">
                  <strong>Schedules:</strong> {package_.scheduleCount}
                </div>
                <div className="mb-2">
                  <strong>Tickets per Schedule:</strong> {package_.ticketCountPerSchedule}
                </div>
                <div className="mb-2">
                  <strong>Total Tickets:</strong> {package_.scheduleCount * package_.ticketCountPerSchedule}
                </div>
                <hr />
                <div className="text-center">
                  <h4 className="text-primary">Total Amount: ₹{package_.totalPrice}</h4>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Payment Methods */}
          <Col md={6}>
            <Card className="mb-4">
              <Card.Header>
                <h5>Payment Method</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="radio"
                    id="upi"
                    name="paymentMethod"
                    label="UPI Payment"
                    checked={paymentMethod === 'upi'}
                    onChange={() => setPaymentMethod('upi')}
                  />
                  <Form.Check
                    type="radio"
                    id="netbanking"
                    name="paymentMethod"
                    label="Net Banking"
                    checked={paymentMethod === 'netbanking'}
                    onChange={() => setPaymentMethod('netbanking')}
                  />
                </Form.Group>

                {paymentMethod === 'upi' && (
                  <div className="text-center mb-3">
                    <div className="mb-3">
                      <strong>UPI ID:</strong> {paymentDetails.upiId}
                    </div>
                    <div className="mb-3">
                      <div style={{ 
                        width: '200px', 
                        height: '200px', 
                        border: '1px solid #ddd', 
                        margin: '0 auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f8f9fa'
                      }}>
                        {paymentDetails.upiQrCode ? (
                          <img 
                            src={paymentDetails.upiQrCode} 
                            alt="UPI QR Code" 
                            style={{ maxWidth: '100%', maxHeight: '100%' }}
                          />
                        ) : (
                          <div className="text-muted">QR Code</div>
                        )}
                      </div>
                    </div>
                    <Button 
                      variant="primary" 
                      href={generateUPILink()}
                      className="mb-3"
                    >
                      <i className="bi bi-phone me-2"></i>
                      Pay with UPI App
                    </Button>
                  </div>
                )}

                {paymentMethod === 'netbanking' && paymentDetails.bankName && (
                  <div className="mb-3">
                    <div><strong>Bank:</strong> {paymentDetails.bankName}</div>
                    <div><strong>Account:</strong> {paymentDetails.accountNumber}</div>
                    <div><strong>IFSC:</strong> {paymentDetails.ifscCode}</div>
                    <div><strong>Account Holder:</strong> {paymentDetails.accountHolderName}</div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Payment Confirmation */}
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <h5>Payment Confirmation</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Transaction ID (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter transaction ID if available"
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Payment Proof (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Describe your payment or paste screenshot details"
                    value={paymentProof}
                    onChange={(e) => setPaymentProof(e.target.value)}
                  />
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button 
                    variant="success" 
                    size="lg"
                    onClick={handlePaymentSubmit}
                    disabled={loading || !selectedGroupId || userGroups.length === 0}
                  >
                    {loading ? 'Submitting...' : 
                     !selectedGroupId && userGroups.length > 1 ? 'Select Group First' :
                     userGroups.length === 0 ? 'No Groups Available' :
                     'I Have Made the Payment'}
                  </Button>
                </div>

                <hr />

                <div className="text-center">
                  <p className="mb-3">Notify admin about your payment:</p>
                  <div className="d-flex gap-2 justify-content-center flex-wrap">
                    <Button 
                      variant="outline-primary"
                      onClick={() => notifyAdmin('sms')}
                      disabled={!transactionId}
                    >
                      <i className="bi bi-chat-text me-2"></i>
                      Send SMS
                    </Button>
                    <Button 
                      variant="outline-success"
                      onClick={() => notifyAdmin('whatsapp')}
                      disabled={!transactionId}
                    >
                      <i className="bi bi-whatsapp me-2"></i>
                      WhatsApp
                    </Button>
                    <Button 
                      variant="outline-info"
                      onClick={() => notifyAdmin('email')}
                      disabled={!transactionId}
                    >
                      <i className="bi bi-envelope me-2"></i>
                      Email
                    </Button>
                  </div>
                  <small className="text-muted d-block mt-2">
                    Enter transaction ID to enable notifications
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Success Modal */}
        <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Payment Submitted Successfully</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <i className="bi bi-check-circle text-success" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3">Payment request submitted!</h5>
              <p>Your payment request has been submitted for admin approval. You will be notified once it's processed.</p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => window.location.href = '/dashboard'}>
              Go to Dashboard
            </Button>
          </Modal.Footer>
        </Modal>
        
        {/* Customer Support Widget */}
        {/* <CustomerSupportWidget position="bottom-right" /> */}
      </Container>
    </PermissionWrapper>
  );
};

export default PaymentPage;